import {
  Button,
  CircularProgress,
  FilterChip,
  Text,
  Tooltip,
} from '@shared/components';
import { ClearAllFiltersChip } from '@shared/components/clear-all-filters-chip/ClearAllFiltersChip';
import { ChatThreadFilter } from '@shared/contexts/ChatWindowContext';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import DataTableFilters from '@shared/features/data-table-filters/DataTableFilters';
import useFilterChips from '@shared/features/data-table-filters/hooks/useFilterChips';
import useFilters from '@shared/features/data-table-filters/hooks/useFilters';
import useSelectedFilterIds, {
  listSelectedOptions,
} from '@shared/features/data-table-filters/hooks/useSelectedFilterIds';
import { DropdownFilterOption } from '@shared/features/data-table-filters/types';
import { DateRangeMenuItem } from '@shared/features/date-filter-popover';
import { GlobalSearchField } from '@shared/features/global-search-field/GlobalSearchField';
import {
  ConversationFilterOptionType,
  ConversationType,
  GetChatThreadsQuery,
  useGetChatThreadsQuery,
  useGetConversationFilterOptionsQuery,
  useHasConciergeActionDefinitionQuery,
} from '@shared/generated/graphql';
import {
  DropdownQueryParam,
  QueryParamName,
} from 'clerk_common/constants/urls/queryParams';
import { filtersToDateRange } from 'clerk_common/stringification/dates';
import clsx from 'clsx';
import moment from 'moment';
import { parseAsArrayOf, parseAsString, useQueryStates } from 'nuqs';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PiMagnifyingGlass, PiPlus, PiWarningCircleFill } from 'react-icons/pi';
import { useRefetchOnActionExecutionUpdates } from '../hooks/useRefetchOnActionExecutionUpdates';
import { ChatScope, getChatScope } from '../utils/chatScope';
import {
  getChatThreadParticipantUserIds,
  getDefaultParticipantUserIds,
  participantOptionsWithCurrentUser,
  shouldDefaultParticipantToCurrentUser,
} from '../utils/chatThreadParticipantFilter';
import { getChatThreadGroupLabel } from '../utils/chatTimestamps';
import { isChatTitlePending, UNTITLED_CHAT_TITLE } from '../utils/chatTitle';
import { ChatScopeMenu } from './ChatScopeMenu';
import { ChatTitleSkeleton } from './ChatTitleSkeleton';

const THREAD_LIST_LIMIT = 100;
const THREAD_LIST_FALLBACK_POLL_INTERVAL_MS = 60 * 1000;
const SEARCH_DEBOUNCE_MS = 300;
const TITLE_FADE_CLASS_NAME =
  '[-webkit-mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)] [mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)]';

type ChatThread = GetChatThreadsQuery['conversations']['edges'][number]['node'];

type ThreadWithLastActivity = {
  conversation: ChatThread;
  lastActivityAt: Date;
};

type ThreadGroup = {
  label: string;
  threads: ThreadWithLastActivity[];
};

type ChatThreadListProps = {
  activeConversationId: string | null;
  onSelect: (conversationId: string) => void;
  onNewChat: () => void;
  canCreateChat: boolean;
  isCompact?: boolean;
  isNewChatActive?: boolean;
  onActiveFiltersChange?: (hasActiveFilters: boolean) => void;
  hasUrlFilters?: boolean;
  pendingConversationId?: string | null;
  onPendingConversationListed?: () => void;
  threadFilter?: ChatThreadFilter | null;
};

type ChatThreadFilters = {
  linkedActionDefinitionIds?: string[];
  movementIds?: string[];
  participantUserIds?: string[];
  searchTerm?: string;
  lastActivityAt?: { start?: string; end?: string };
  hasActiveFilters: boolean;
  isReady: boolean;
};

type FilteredChatThreadListProps = Omit<
  ChatThreadListProps,
  'hasUrlFilters' | 'onActiveFiltersChange' | 'threadFilter'
>;

type ChatThreadListViewProps = FilteredChatThreadListProps & {
  filters: ChatThreadFilters;
  toolbar?: ReactNode;
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeout);
  }, [value, delayMs]);
  return debounced;
}

function toDateFilterInput({
  start,
  end,
}: {
  start?: string;
  end?: string;
}): { start?: string; end?: string } | undefined {
  return start || end ? { start, end } : undefined;
}

type DefaultSop = { id: string; name: string };

// NOTE(mike): Vee's escalation handler is its own SOP, so its chats are part of
// the Vee default alongside the concierge chats.
function useDefaultSops() {
  const { defaultOrgId } = useMeContext();
  const { data, loading } = useHasConciergeActionDefinitionQuery({
    variables: { input: { organizationId: defaultOrgId ?? '' } },
    skip: !defaultOrgId,
  });
  const vee = data?.hasConciergeActionDefinition;
  const defaultSops = useMemo<DefaultSop[]>(
    () => [
      ...(vee?.actionDefinitionId
        ? [
            {
              id: vee.actionDefinitionId,
              name: vee.actionDefinitionName ?? 'Vee',
            },
          ]
        : []),
      ...(vee?.escalationHandlerActionDefinitionId
        ? [
            {
              id: vee.escalationHandlerActionDefinitionId,
              name:
                vee.escalationHandlerActionDefinitionName ?? 'Vee escalations',
            },
          ]
        : []),
    ],
    [vee]
  );
  return {
    defaultSops,
    defaultSopIds: useMemo(
      () => defaultSops.map(({ id }) => id),
      [defaultSops]
    ),
    isDefaultSopsLoading: loading,
  };
}

function isSameIdSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

export function ChatThreadList({
  hasUrlFilters = true,
  onActiveFiltersChange,
  threadFilter,
  ...props
}: ChatThreadListProps) {
  if (threadFilter) {
    return <ScopedChatThreadList {...props} threadFilter={threadFilter} />;
  }
  return hasUrlFilters ? (
    <UrlFilteredChatThreadList
      {...props}
      onActiveFiltersChange={onActiveFiltersChange}
    />
  ) : (
    <DefaultFilteredChatThreadList {...props} />
  );
}

// NOTE(jonty): A scoped list shows every chat about its entities, whoever
// started it and whichever SOP ran, so nothing about them is hidden.
function ScopedChatThreadList({
  threadFilter,
  ...props
}: FilteredChatThreadListProps & { threadFilter: ChatThreadFilter }) {
  return (
    <ChatThreadListView
      {...props}
      filters={{
        movementIds: threadFilter.movementIds,
        hasActiveFilters: true,
        isReady: true,
      }}
      toolbar={
        <Text type="body-xs" className="shrink-0 px-4 pt-3 text-gray-500">
          Chats about this load
        </Text>
      }
    />
  );
}

// NOTE(wassim): Filter params are global URL keys, so a list embedded beside
// another page's table must not read or write them.
function DefaultFilteredChatThreadList(props: FilteredChatThreadListProps) {
  const { me } = useMeContext();
  const { defaultSopIds, isDefaultSopsLoading } = useDefaultSops();

  return (
    <ChatThreadListView
      {...props}
      filters={{
        linkedActionDefinitionIds: defaultSopIds.length
          ? defaultSopIds
          : undefined,
        participantUserIds: me?.id ? [me.id] : undefined,
        hasActiveFilters: false,
        isReady: !isDefaultSopsLoading && Boolean(me?.id),
      }}
    />
  );
}

function UrlFilteredChatThreadList({
  onActiveFiltersChange,
  ...props
}: Omit<ChatThreadListProps, 'hasUrlFilters'>) {
  const { isCompact = false } = props;
  const { defaultOrgId, me } = useMeContext();
  const { handleSelectMany, clearFilters } = useFilters();
  const { defaultSops, defaultSopIds, isDefaultSopsLoading } = useDefaultSops();

  const selectedSopIds = useSelectedFilterIds(
    DropdownQueryParam.ACTION_DEFINITION_ID
  );
  const selectedParticipantIds = useSelectedFilterIds(
    DropdownQueryParam.USER_ID
  );
  const lastActiveStart = useSelectedFilterIds(
    DropdownQueryParam.LAST_ACTIVE_START_DATE
  );
  const lastActiveEnd = useSelectedFilterIds(
    DropdownQueryParam.LAST_ACTIVE_END_DATE
  );

  const [searchParams, setSearchParams] = useQueryStates({
    [QueryParamName.SEARCH_TERM]: parseAsString,
    [DropdownQueryParam.ACTION_DEFINITION_ID]: parseAsArrayOf(parseAsString),
    [DropdownQueryParam.USER_ID]: parseAsArrayOf(parseAsString),
  });
  const searchTerm = searchParams[QueryParamName.SEARCH_TERM] ?? '';
  const [isSearchOpen, setIsSearchOpen] = useState(Boolean(searchTerm));
  const toggleSearch = () => {
    if (isSearchOpen) {
      void setSearchParams(
        { [QueryParamName.SEARCH_TERM]: null },
        { shallow: true }
      );
    }
    setIsSearchOpen(!isSearchOpen);
  };
  const participantQueryParams = searchParams[DropdownQueryParam.USER_ID];
  const sopQueryParams = searchParams[DropdownQueryParam.ACTION_DEFINITION_ID];
  const debouncedSearchTerm = useDebouncedValue(
    searchTerm.trim(),
    SEARCH_DEBOUNCE_MS
  );

  // NOTE(parlato): Default to the org's chat SOP (Vee) so exception-handler
  // chats don't drown out real chats.
  // NOTE(wassim): Defaults apply whenever the param is absent from the URL, so a
  // bare /chats link restores them; an explicitly emptied param (All chats,
  // dismissing the SOP) stays cleared.
  useEffect(() => {
    if (
      isDefaultSopsLoading ||
      defaultSopIds.length === 0 ||
      sopQueryParams !== null
    ) {
      return;
    }
    void handleSelectMany({
      queryParam: DropdownQueryParam.ACTION_DEFINITION_ID,
      ids: defaultSopIds,
    });
  }, [defaultSopIds, handleSelectMany, isDefaultSopsLoading, sopQueryParams]);

  useEffect(() => {
    if (
      !me?.id ||
      !shouldDefaultParticipantToCurrentUser(participantQueryParams)
    ) {
      return;
    }
    void handleSelectMany({
      queryParam: DropdownQueryParam.USER_ID,
      ids: [me.id],
    });
  }, [handleSelectMany, me?.id, participantQueryParams]);

  const effectiveSopIds =
    sopQueryParams === null && defaultSopIds.length
      ? defaultSopIds
      : selectedSopIds;

  const { data: sopOptionsData, loading: isSopOptionsLoading } =
    useGetConversationFilterOptionsQuery({
      variables: {
        input: {
          organizationId: defaultOrgId ?? '',
          type: ConversationFilterOptionType.LINKED_ACTION_DEFINITION,
          conversationTypes: [ConversationType.CHAT],
        },
      },
      skip: !defaultOrgId,
    });

  const { data: participantOptionsData, loading: isParticipantOptionsLoading } =
    useGetConversationFilterOptionsQuery({
      variables: {
        input: {
          organizationId: defaultOrgId ?? '',
          type: ConversationFilterOptionType.PARTICIPANT,
          conversationTypes: [ConversationType.CHAT],
        },
      },
      skip: !defaultOrgId || isCompact,
    });

  const sopFilterOption = useMemo<DropdownFilterOption>(() => {
    const options = [
      ...(sopOptionsData?.conversationFilterOptions.options ?? []),
    ].map(({ label, value }) => ({ label, value }));
    const missingDefaultSops = defaultSops
      .filter(({ id }) => !options.some((option) => option.value === id))
      .map(({ id, name }) => ({ label: name, value: id }));
    options.unshift(...missingDefaultSops);
    return {
      label: 'SOP',
      type: DropdownQueryParam.ACTION_DEFINITION_ID,
      showSearch: true,
      options,
    };
  }, [sopOptionsData, defaultSops]);

  const participantFilterOption = useMemo<DropdownFilterOption>(
    () => ({
      label: 'People',
      type: DropdownQueryParam.USER_ID,
      showSearch: true,
      options: participantOptionsWithCurrentUser(
        [
          ...(participantOptionsData?.conversationFilterOptions.options ?? []),
        ].map(({ label, value }) => ({ label, value })),
        me
      ),
    }),
    [me, participantOptionsData]
  );

  const setSopIds = useCallback(
    (ids: string[] | null) =>
      void setSearchParams(
        { [DropdownQueryParam.ACTION_DEFINITION_ID]: ids },
        { shallow: true }
      ),
    [setSearchParams]
  );
  const setParticipantIds = useCallback(
    (ids: string[] | null) =>
      void setSearchParams(
        { [DropdownQueryParam.USER_ID]: ids },
        { shallow: true }
      ),
    [setSearchParams]
  );
  // NOTE(wassim): Switching orgs remounts this list with the previous org's
  // filter ids still in the URL, so ids the current org lacks are dropped.
  useDropUnknownFilterIds({
    selectedIds: sopQueryParams,
    options: sopFilterOption.options,
    isReady:
      Boolean(sopOptionsData) && !isSopOptionsLoading && !isDefaultSopsLoading,
    onChange: setSopIds,
  });
  useDropUnknownFilterIds({
    selectedIds: participantQueryParams,
    options: participantFilterOption.options,
    isReady:
      !isCompact &&
      Boolean(participantOptionsData) &&
      !isParticipantOptionsLoading,
    onChange: setParticipantIds,
  });

  const filterOptions = useMemo(() => [sopFilterOption], [sopFilterOption]);

  const defaultedParticipantIds = getDefaultParticipantUserIds({
    currentUserId: me?.id,
    selectedParticipantIds,
    userIdQueryParams: participantQueryParams,
  });
  const participantUserIds = getChatThreadParticipantUserIds({
    currentUserId: me?.id,
    isCompact,
    selectedParticipantIds: defaultedParticipantIds,
  });
  const isWaitingForCurrentUserDefault =
    !me?.id &&
    (isCompact ||
      shouldDefaultParticipantToCurrentUser(participantQueryParams));

  const lastActivityRange = filtersToDateRange(lastActiveStart, lastActiveEnd);

  const scope = getChatScope(defaultedParticipantIds, me?.id);
  const handleScopeChange = (nextScope: Exclude<ChatScope, 'custom'>) => {
    if (nextScope === 'mine' && me?.id) {
      void handleSelectMany({
        queryParam: DropdownQueryParam.USER_ID,
        ids: [me.id],
      });
      return;
    }
    void clearFilters([DropdownQueryParam.USER_ID]);
  };
  // NOTE(wassim): Participants live in the scope menu and the Vee SOP default
  // is the baseline, so neither shows as a chip or counts as an active filter.
  const isDefaultSopSelection =
    defaultSopIds.length > 0 && isSameIdSet(effectiveSopIds, defaultSopIds);

  const selectedFilters = useFilterChips([
    {
      queryParam: DropdownQueryParam.ACTION_DEFINITION_ID,
      selectedIds: isDefaultSopSelection
        ? []
        : listSelectedOptions(sopFilterOption, selectedSopIds),
    },
    {
      queryParam: DropdownQueryParam.LAST_ACTIVE_START_DATE,
      selectedIds: formatDateRangeChip(lastActivityRange),
    },
  ]);

  const hasActiveFilters =
    (!isDefaultSopSelection && effectiveSopIds.length > 0) ||
    (!isCompact && scope === 'custom') ||
    Boolean(debouncedSearchTerm) ||
    Boolean(toDateFilterInput(lastActivityRange));

  useEffect(() => {
    onActiveFiltersChange?.(hasActiveFilters);
  }, [hasActiveFilters, onActiveFiltersChange]);

  return (
    <ChatThreadListView
      {...props}
      filters={{
        linkedActionDefinitionIds: effectiveSopIds.length
          ? effectiveSopIds
          : undefined,
        participantUserIds,
        searchTerm: debouncedSearchTerm || undefined,
        lastActivityAt: toDateFilterInput(lastActivityRange),
        hasActiveFilters,
        isReady: !isDefaultSopsLoading && !isWaitingForCurrentUserDefault,
      }}
      toolbar={
        <div className="shrink-0 px-2 py-1.5">
          <div className="flex items-center gap-1">
            {!isCompact && (
              <ChatScopeMenu
                onScopeChange={handleScopeChange}
                participantCount={defaultedParticipantIds.length}
                participantFilterOption={participantFilterOption}
                scope={scope}
              />
            )}
            <div className="min-w-0 flex-1">
              {isSearchOpen && (
                <GlobalSearchField
                  hasIcon={false}
                  placeholder="Search chats"
                  size="xs"
                />
              )}
            </div>
            <Button
              aria-label={isSearchOpen ? 'Close search' : 'Search chats'}
              aria-pressed={isSearchOpen}
              icon={<PiMagnifyingGlass size={16} />}
              isGhost
              size="xs"
              onPress={toggleSearch}
            />
            <DataTableFilters
              isCompact
              options={filterOptions}
              triggerOptions={{ isGhost: true, label: null }}
            >
              <DateRangeMenuItem
                label="Last active"
                startDateQueryParam={DropdownQueryParam.LAST_ACTIVE_START_DATE}
                endDateQueryParam={DropdownQueryParam.LAST_ACTIVE_END_DATE}
              />
            </DataTableFilters>
          </div>
          {selectedFilters.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              {selectedFilters.map((filter, index) => (
                <FilterChip
                  {...filter}
                  className="!px-1.5 !py-0.5"
                  key={`filter_chip_${index}`}
                />
              ))}
              <ClearAllFiltersChip className="!px-1.5 !py-0.5" />
            </div>
          )}
        </div>
      }
    />
  );
}

function useDropUnknownFilterIds({
  selectedIds,
  options,
  isReady,
  onChange,
}: {
  selectedIds: string[] | null;
  options: DropdownFilterOption['options'];
  isReady: boolean;
  onChange: (ids: string[] | null) => void;
}) {
  useEffect(() => {
    if (!isReady || !selectedIds) return;
    const knownIds = new Set(options.map(({ value }) => value));
    const keptIds = selectedIds.filter((id) => knownIds.has(id));
    if (keptIds.length === selectedIds.length) return;
    onChange(keptIds.length > 0 ? keptIds : null);
  }, [isReady, onChange, options, selectedIds]);
}

function ChatThreadListView({
  activeConversationId,
  onSelect,
  onNewChat,
  canCreateChat,
  isNewChatActive = false,
  pendingConversationId = null,
  onPendingConversationListed,
  filters,
  toolbar,
}: ChatThreadListViewProps) {
  const { defaultOrgId } = useMeContext();
  const { data, loading, refetch } = useGetChatThreadsQuery({
    variables: {
      input: {
        organizationIds: defaultOrgId ? [defaultOrgId] : [],
        types: [ConversationType.CHAT],
        linkedActionDefinitionIds: filters.linkedActionDefinitionIds,
        movementIds: filters.movementIds,
        participantUserIds: filters.participantUserIds,
        searchTerm: filters.searchTerm,
        lastActivityAt: filters.lastActivityAt,
      },
      limit: THREAD_LIST_LIMIT,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: THREAD_LIST_FALLBACK_POLL_INTERVAL_MS,
    skip: !defaultOrgId || !filters.isReady,
  });

  // NOTE(wassim): Conversations with no messages are abandoned drafts from
  // before the composer existed; only the selected one is worth showing.
  const sortedThreads = useMemo<ThreadWithLastActivity[]>(() => {
    const conversations =
      data?.conversations.edges.map(({ node }) => node) ?? [];
    const isPendingChatMissing =
      pendingConversationId !== null &&
      pendingConversationId === activeConversationId &&
      !conversations.some(({ id }) => id === pendingConversationId);
    return [
      ...(isPendingChatMissing
        ? [buildPendingThread(pendingConversationId, defaultOrgId ?? '')]
        : []),
      ...conversations,
    ]
      .filter(
        (conversation) =>
          conversation.latestExternalMessage ||
          conversation.id === activeConversationId
      )
      .map((conversation) => ({
        conversation,
        lastActivityAt: getLastActivityDate(conversation),
      }))
      .sort((a, b) => b.lastActivityAt.getTime() - a.lastActivityAt.getTime());
  }, [activeConversationId, data, defaultOrgId, pendingConversationId]);
  const isPendingChatListed = Boolean(
    pendingConversationId &&
      data?.conversations.edges.some(
        ({ node }) => node.id === pendingConversationId
      )
  );
  useEffect(() => {
    if (isPendingChatListed) onPendingConversationListed?.();
  }, [isPendingChatListed, onPendingConversationListed]);
  const threadGroups = useMemo(
    () => groupThreadsByRecency(sortedThreads),
    [sortedThreads]
  );
  const listedConversationIds = useMemo(
    () => sortedThreads.map(({ conversation }) => conversation.id),
    [sortedThreads]
  );
  useRefetchOnActionExecutionUpdates(listedConversationIds, refetch);
  // NOTE(wassim): An owner line on every row says nothing when they all match
  // (all mine, or all Vee's), so it only appears once owners differ.
  const hasMixedOwners = useMemo(
    () =>
      new Set(
        sortedThreads
          .map(({ conversation }) => getOwnerName(conversation))
          .filter(Boolean)
      ).size > 1,
    [sortedThreads]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {canCreateChat && (
        <div className="shrink-0 p-2 pb-0">
          <button
            type="button"
            aria-pressed={isNewChatActive}
            onClick={onNewChat}
            className={`flex h-9 w-full items-center gap-3 rounded-lg px-3 text-left transition-colors ${
              isNewChatActive ? 'bg-gray-100' : 'hover:bg-gray-50'
            }`}
          >
            <span
              aria-hidden
              className={`text-dark flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                isNewChatActive ? '' : 'bg-gray-100'
              }`}
            >
              <PiPlus size={12} />
            </span>
            <Text type="body-sm" className="text-dark">
              New chat
            </Text>
          </button>
        </div>
      )}
      {toolbar}
      {!defaultOrgId || (loading && sortedThreads.length === 0) ? (
        <div className="flex flex-1 items-center justify-center">
          <CircularProgress />
        </div>
      ) : sortedThreads.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-4">
          <Text type="body-sm" className="text-center text-gray-500">
            {filters.hasActiveFilters
              ? 'No chats match the current filters.'
              : 'No chats in this organization yet.'}
          </Text>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-2 pt-0">
          {threadGroups.map(({ label, threads }) => (
            <section key={label} aria-label={label}>
              <Text
                type="body-sm"
                className="px-2 pb-1 pt-3 !text-[13px] text-gray-400"
              >
                {label}
              </Text>
              <ul className="space-y-0.5">
                {threads.map(({ conversation }) => (
                  <ChatThreadListItem
                    key={conversation.id}
                    conversation={conversation}
                    isActive={conversation.id === activeConversationId}
                    showOwnerName={hasMixedOwners}
                    onSelect={() => onSelect(conversation.id)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function groupThreadsByRecency(
  threads: ThreadWithLastActivity[]
): ThreadGroup[] {
  const groups: ThreadGroup[] = [];
  for (const thread of threads) {
    const label = getChatThreadGroupLabel(thread.lastActivityAt);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.label === label) {
      lastGroup.threads.push(thread);
    } else {
      groups.push({ label, threads: [thread] });
    }
  }
  return groups;
}

function formatDateRangeChip({
  start,
  end,
}: {
  start?: string;
  end?: string;
}): string[] | undefined {
  if (!start || !end) return undefined;
  return [`${moment.utc(start).format('l')} - ${moment.utc(end).format('l')}`];
}

type ChatThreadListItemProps = {
  conversation: ChatThread;
  isActive: boolean;
  showOwnerName: boolean;
  onSelect: () => void;
};

function ChatThreadListItem({
  conversation,
  isActive,
  showOwnerName,
  onSelect,
}: ChatThreadListItemProps) {
  const title = conversation.title?.trim();
  const isTitlePending = isChatTitlePending(
    title,
    new Date(conversation.createdAt)
  );
  const ownerName = showOwnerName ? getOwnerName(conversation) : undefined;

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={`flex min-h-9 w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left transition-colors ${
          isActive ? 'bg-gray-100' : 'hover:bg-gray-50'
        }`}
      >
        <div className="min-w-0 flex-1">
          {isTitlePending ? (
            <ChatTitleSkeleton className="w-3/5" />
          ) : (
            <TruncatedTitle title={title || UNTITLED_CHAT_TITLE} />
          )}
          {ownerName && (
            <Text type="body-xs" className="truncate text-gray-500">
              {ownerName}
            </Text>
          )}
        </div>
        <StatusColumn className="w-3.5">
          {(conversation.handledOpenEscalations?.length ?? 0) > 0 && (
            <EscalationFlag />
          )}
        </StatusColumn>
        <StatusColumn className="w-[7px]">
          <ActivityDot
            isUnread={Boolean(conversation.hasUnreadMessages) && !isActive}
            isWorking={Boolean(conversation.hasActionExecutionInProgress)}
          />
        </StatusColumn>
      </button>
    </li>
  );
}

// NOTE(mike): Each status keeps a fixed-width slot, even when empty, so flags
// and dots line up in columns down the list.
function StatusColumn({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  return (
    <span className={clsx('flex shrink-0 justify-center', className)}>
      {children}
    </span>
  );
}

function EscalationFlag() {
  return (
    <Tooltip content="Open escalation">
      <span role="img" aria-label="Open escalation" className="flex">
        <PiWarningCircleFill className="text-orange-500" size={14} />
      </span>
    </Tooltip>
  );
}

function ActivityDot({
  isUnread,
  isWorking,
}: {
  isUnread: boolean;
  isWorking: boolean;
}) {
  if (!isUnread && !isWorking) return null;
  return (
    <span
      role="status"
      aria-label={isWorking ? 'Working' : 'Unread messages'}
      className={clsx(
        'h-[7px] w-[7px] shrink-0 rounded-full',
        isWorking
          ? 'animate-pulse bg-gray-400 motion-reduce:animate-none'
          : 'bg-blue-500'
      )}
    />
  );
}

function TruncatedTitle({ title }: { title: string }) {
  const titleRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = titleRef.current;
    if (!element) return;
    const measure = () =>
      setIsTruncated(element.scrollWidth > element.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [title]);

  const label = (
    <Text
      ref={titleRef}
      as="span"
      type="body-sm"
      className={`text-dark block overflow-hidden whitespace-nowrap !leading-5 ${
        isTruncated ? TITLE_FADE_CLASS_NAME : ''
      }`}
    >
      {title}
    </Text>
  );
  return isTruncated ? <Tooltip content={title}>{label}</Tooltip> : label;
}

function getOwnerName(conversation: ChatThread): string | undefined {
  const owner = conversation.user;
  if (!owner) return undefined;
  const fullName = [owner.firstName, owner.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || owner.email || undefined;
}

function buildPendingThread(id: string, organizationId: string): ChatThread {
  return {
    __typename: 'Conversation',
    id,
    organizationId,
    title: null,
    createdAt: new Date().toISOString(),
    hasUnreadMessages: false,
    hasActionExecutionInProgress: true,
    handledOpenEscalations: [],
    user: null,
    latestExternalMessage: null,
  };
}

function getLastActivityDate(conversation: ChatThread): Date {
  return new Date(
    conversation.latestExternalMessage?.createdAt ?? conversation.createdAt
  );
}
